import React, { useState, useEffect, useCallback, useMemo } from 'react'
import { FileUp, Search, Calendar, X, ArrowUp, ArrowDown } from 'lucide-react'
import { getLocalApiUrl, authFetch } from '../config/api'
import { displayMembershipNumber, displayPlayerId } from '../utils/patronIdentity'
import { formatSydneyDateTime } from '../utils/sydneyDateTime'

const P = { pri: '#0a5169', priM: '#e8f2f6' }
const fmt$ = (n) => '$' + (Number(n) ?? 0).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })

const HOUR_OPTIONS = Array.from({ length: 24 }, (_, h) => ({
  value: String(h),
  label: `${String(h).padStart(2, '0')}:00`,
}))

function localDateStr(d = new Date()) {
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

const typeLabel = (t) => {
  if (t === 'payout') return 'Payout'
  if (t === 'combined_session') return 'Combined session'
  if (t === 'combined_interval') return 'Combined interval'
  if (t === 'slot_jackpot') return 'Slot jackpot'
  if (t === 'session') return 'Session'
  if (t === 'transaction') return 'Transaction'
  return t || 'Item'
}

const formatRowId = (it) => {
  if (it.type === 'combined_session') return it.sessionpk || it.SessionPK || it.id
  if (it.type === 'combined_interval') return `${it.sessionpk || it.SessionPK || ''} · ${(it.intervalstart || it.SnapShotTime || '').toString().slice(11, 16)}`
  if (it.type === 'slot_jackpot') return `${it.EgmHouseNo || it.egm_pk || it.egm_id || 'EGM'} · ${it.gaming_date || it.trading_day || ''}`
  if (it.type === 'payout') return it.payout_id || it.id
  return it.id
}

function normalizeContextType(type) {
  const t = String(type || '').toLowerCase()
  if (t === 'investigation' || t === 'investigation_case') return 'investigation_case'
  if (t === 'ecdd') return 'ecdd'
  if (t === 'payout' || t === 'payout_record') return 'payout'
  return t
}

export function buildEvidenceBulkItem(it) {
  const payload = { ...it }
  delete payload._memberLabel
  if (it.type === 'payout') {
    return {
      type: 'payout',
      source_id: it.payout_id || it.id,
      payout_id: it.payout_id || it.id,
      payload,
    }
  }
  if (it.type === 'combined_session') {
    return {
      type: 'combined_session',
      sessionpk: it.sessionpk || it.SessionPK,
      trading_day: it.trading_day,
      expand_intervals: true,
      payload,
    }
  }
  if (it.type === 'combined_interval') {
    return {
      type: 'combined_interval',
      sessionpk: it.sessionpk || it.SessionPK,
      trading_day: it.trading_day,
      intervalstart: it.intervalstart || it.IntervalStart || it.SnapShotTime,
      payload,
    }
  }
  if (it.type === 'slot_jackpot') {
    return {
      type: 'slot_jackpot',
      source_id: it.source_id || it.id,
      source_table: 'fact_slot_analysis_daily',
      payload,
    }
  }
  const isSession = it.type === 'session'
  const isPayout = it.type === 'payout'
  return {
    type: isSession ? 'session' : isPayout ? 'payout' : 'transaction',
    payload,
    source_table: isSession ? 'gaming_sessions' : isPayout ? 'payout_records' : 'gaming_transactions',
    ...(isSession ? { SessionPK: it.id } : isPayout ? { payout_id: it.id, PayoutID: it.id } : { TransactionPK: it.id }),
  }
}

/**
 * Shared evidence browser — Investigation Link Transaction, ECDD activity, Tools browse, Payout linking.
 * @param {object} props
 * @param {{ type: 'investigation_case'|'ecdd'|'payout', id: string }} props.context
 * @param {'modal'|'embedded'} [props.display]
 * @param {string[]} [props.lanes] — restrict lanes (default all; payout defaults to combined+jackpots)
 * @param {boolean} [props.seedMemberFilter] — when false, member box starts blank (browse across)
 */
export default function EvidenceLinkBrowser({
  context,
  display = 'modal',
  isOpen = true,
  onClose,
  onLinked,
  defaultMemberNumber = '',
  defaultPlayerId = '',
  defaultMemberName = '',
  defaultEgmHouseNo = '',
  defaultLane = 'payouts',
  stepId = null,
  showCsvTab = null,
  title = null,
  lanes = null,
  seedMemberFilter = true,
}) {
  const contextType = normalizeContextType(context?.type)
  const contextId = context?.id
  const isInvestigation = contextType === 'investigation_case'
  const isPayout = contextType === 'payout'
  const csvEnabled = showCsvTab ?? isInvestigation
  const modalTitle = title || (isInvestigation ? 'Link Evidence' : isPayout ? 'Browse floor activity' : 'Review & link activity')
  const availableLanes = Array.isArray(lanes) && lanes.length
    ? lanes
    : isPayout
      ? ['combined', 'jackpots']
      : ['payouts', 'combined', 'jackpots']
  const initialLane = availableLanes.includes(defaultLane) ? defaultLane : availableLanes[0]

  const [tab, setTab] = useState('browse')
  const [lane, setLane] = useState(initialLane)
  const [combinedView, setCombinedView] = useState('session')
  const [dateFrom, setDateFrom] = useState('')
  const [dateTo, setDateTo] = useState('')
  const [preset, setPreset] = useState('1month')
  const [dateMode, setDateMode] = useState('range') // 'range' | 'trading_day'
  const [tradingDay, setTradingDay] = useState('')
  const [hourFrom, setHourFrom] = useState('')
  const [hourTo, setHourTo] = useState('')
  const [sortKey, setSortKey] = useState('date')
  const [sortDir, setSortDir] = useState('desc')
  const [memberFilter, setMemberFilter] = useState('')
  const [playerIdFilter, setPlayerIdFilter] = useState('')
  const [egmFilter, setEgmFilter] = useState('')
  const [includeUnidentified, setIncludeUnidentified] = useState(false)
  const [items, setItems] = useState([])
  const [browseHint, setBrowseHint] = useState(null)
  const [expandedSessions, setExpandedSessions] = useState(() => new Set())
  const [selected, setSelected] = useState(new Set())
  const [loading, setLoading] = useState(false)
  const [adding, setAdding] = useState(false)
  const [csvFile, setCsvFile] = useState(null)
  const [csvPreview, setCsvPreview] = useState([])
  const [error, setError] = useState(null)

  const active = display === 'embedded' || isOpen

  useEffect(() => {
    if (active && seedMemberFilter && defaultMemberNumber) setMemberFilter(String(defaultMemberNumber))
    if (active && seedMemberFilter && defaultPlayerId) setPlayerIdFilter(String(defaultPlayerId))
    if (active && defaultEgmHouseNo) setEgmFilter(String(defaultEgmHouseNo))
  }, [active, defaultMemberNumber, defaultPlayerId, defaultEgmHouseNo, seedMemberFilter])

  useEffect(() => {
    if (active) setLane(initialLane)
  }, [active, initialLane, contextId])

  // Default: hide uncarded when searching by membership / player id
  useEffect(() => {
    if (!active || lane !== 'combined') return
    if (memberFilter.trim() || playerIdFilter.trim()) {
      setIncludeUnidentified(false)
    }
  }, [active, lane, memberFilter, playerIdFilter])

  const applyPreset = useCallback((p) => {
    setDateMode('range')
    setPreset(p)
    setTradingDay('')
    const d = new Date()
    const to = localDateStr(d)
    let from
    if (p === '7days') {
      d.setDate(d.getDate() - 7)
      from = localDateStr(d)
    } else if (p === '2weeks') {
      d.setDate(d.getDate() - 14)
      from = localDateStr(d)
    } else {
      d.setMonth(d.getMonth() - 1)
      from = localDateStr(d)
    }
    setDateFrom(from)
    setDateTo(to)
  }, [])

  useEffect(() => {
    if (preset && active && dateMode === 'range') applyPreset(preset)
  }, [preset, active, applyPreset, dateMode])

  const loadLinkable = useCallback(async () => {
    if (!contextId || !contextType) return
    setLoading(true)
    setError(null)
    setBrowseHint(null)
    setSelected(new Set())
    setExpandedSessions(new Set())
    try {
      const params = new URLSearchParams()
      params.set('context', contextType)
      params.set('context_id', String(contextId))
      params.set('lane', lane)
      if (lane === 'combined') {
        params.set('view', combinedView)
        if (combinedView === 'session') params.set('include_intervals', 'true')
      }
      if (dateMode === 'trading_day' && tradingDay) {
        params.set('trading_day', tradingDay)
      } else {
        if (dateFrom) params.set('date_from', dateFrom)
        if (dateTo) params.set('date_to', dateTo)
        if (preset) params.set('preset', preset)
      }
      if (lane !== 'jackpots') {
        if (hourFrom !== '') params.set('hour_from', hourFrom)
        if (hourTo !== '') params.set('hour_to', hourTo)
      }
      if (memberFilter.trim()) params.set('member_number', memberFilter.trim())
      if (playerIdFilter.trim()) params.set('player_id', playerIdFilter.trim())
      if (egmFilter.trim()) params.set('egm_pk', egmFilter.trim())
      if (includeUnidentified) params.set('include_unidentified', 'true')
      const res = await authFetch(getLocalApiUrl(`/api/compliance/linkable-evidence?${params}`))
      const json = await res.json()
      if (json.success) {
        setItems(json.data || [])
        setBrowseHint(json.hint || null)
      } else {
        setError(json.error || 'Failed to load')
        setItems([])
        setBrowseHint(null)
      }
    } catch (e) {
      setError(e.message || 'Failed to load')
      setItems([])
    } finally {
      setLoading(false)
    }
  }, [contextId, contextType, lane, combinedView, dateMode, tradingDay, dateFrom, dateTo, preset, hourFrom, hourTo, memberFilter, playerIdFilter, egmFilter, includeUnidentified])

  useEffect(() => {
    if (active && tab === 'browse' && contextId) loadLinkable()
  }, [active, tab, contextId, lane, combinedView, loadLinkable])

  const toggleSelect = (id) => {
    setSelected(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const itemAmountNum = (it) => {
    if (it.type === 'slot_jackpot') return Number(it.jackpot_win ?? it.jackpot_win_aud ?? it.amount ?? 0)
    if (it.type === 'combined_session' || it.type === 'combined_interval') {
      return Number(it.CreditWin ?? it.creditwin_aud ?? it.CashOut ?? 0)
    }
    return Number(it.CashOut || it.CashIn || it.amount || it.Turnover || 0)
  }

  const itemDateMs = (it) => {
    if (it.type === 'slot_jackpot') {
      const d = it.gaming_date || it.trading_day
      if (!d) return 0
      const t = new Date(`${d}T12:00:00`).getTime()
      return Number.isFinite(t) ? t : 0
    }
    const raw = it.SnapShotTime || it.StartTime || it.SessionStart || it.intervalstart || it.IntervalStart || ''
    if (!raw) return 0
    const d = new Date(typeof raw === 'string' && !raw.includes('T') ? String(raw).replace(' ', 'T') : raw)
    return Number.isFinite(d.getTime()) ? d.getTime() : 0
  }

  const sortedItems = useMemo(() => {
    const list = [...items]
    const dir = sortDir === 'asc' ? 1 : -1
    list.sort((a, b) => {
      let cmp = 0
      if (sortKey === 'date') cmp = itemDateMs(a) - itemDateMs(b)
      else if (sortKey === 'amount') cmp = itemAmountNum(a) - itemAmountNum(b)
      else if (sortKey === 'member') {
        cmp = String(displayMembershipNumber(a) || a.membership_number || '').localeCompare(
          String(displayMembershipNumber(b) || b.membership_number || '')
        )
      } else if (sortKey === 'player') {
        cmp = String(displayPlayerId(a) || a.player_id || a.MemberID || '').localeCompare(
          String(displayPlayerId(b) || b.player_id || b.MemberID || '')
        )
      } else if (sortKey === 'egm') {
        cmp = String(a.HouseNumber || a.EgmHouseNo || a.EgmPK || a.egm_pk || '').localeCompare(
          String(b.HouseNumber || b.EgmHouseNo || b.EgmPK || b.egm_pk || '')
        )
      } else if (sortKey === 'venue') {
        cmp = String(a.Venue || '').localeCompare(String(b.Venue || ''))
      } else if (sortKey === 'type') {
        cmp = String(a.type || '').localeCompare(String(b.type || ''))
      } else if (sortKey === 'id') {
        cmp = String(formatRowId(a)).localeCompare(String(formatRowId(b)))
      } else if (sortKey === 'intervals') {
        cmp = Number(a.interval_count || 0) - Number(b.interval_count || 0)
      }
      return cmp * dir
    })
    return list
  }, [items, sortKey, sortDir])

  const toggleSort = (key) => {
    if (sortKey === key) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'))
    else {
      setSortKey(key)
      setSortDir(key === 'date' || key === 'amount' ? 'desc' : 'asc')
    }
  }

  const SortTh = ({ col, children, className = 'text-left' }) => {
    const activeCol = sortKey === col
    return (
      <th className={`px-3 py-2 font-medium text-slate-600 ${className}`}>
        <button
          type="button"
          onClick={() => toggleSort(col)}
          className="inline-flex items-center gap-1 hover:text-slate-900"
        >
          {children}
          {activeCol ? (sortDir === 'asc' ? <ArrowUp size={12} /> : <ArrowDown size={12} />) : (
            <span className="text-slate-300 text-[10px]">↕</span>
          )}
        </button>
      </th>
    )
  }

  const selectAll = () => {
    if (selected.size === sortedItems.length) setSelected(new Set())
    else setSelected(new Set(sortedItems.map(i => i.id)))
  }

  const handleAddSelected = async () => {
    if (!contextId || selected.size === 0) return
    setAdding(true)
    setError(null)
    try {
      const bulkItems = []
      for (const id of selected) {
        const it = items.find(x => x.id === id)
        if (!it) continue
        bulkItems.push(buildEvidenceBulkItem(it))
      }

      let json
      if (isInvestigation) {
        const body = { items: bulkItems }
        if (stepId) body.step_id = stepId
        const res = await authFetch(getLocalApiUrl(`/api/cases/${contextId}/evidence/add`), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        })
        json = await res.json()
      } else if (isPayout) {
        const res = await authFetch(
          getLocalApiUrl(`/api/monitoring/payouts/${encodeURIComponent(String(contextId))}/link-transactions`),
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              items: bulkItems.map((b) => ({
                ...b,
                ...(b.payload || {}),
                type: b.type,
              })),
              remarks: 'Payout modal browse / link',
            }),
          }
        )
        json = await res.json()
      } else {
        const res = await authFetch(getLocalApiUrl('/api/compliance/evidence/link'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            context: { type: 'ecdd', id: String(contextId) },
            items: bulkItems,
            remarks: 'ECDD activity analysis',
          }),
        })
        json = await res.json()
      }

      if (json.success) {
        setSelected(new Set())
        onLinked?.(json.data || { added: bulkItems.length })
        if (display === 'modal') onClose?.()
      } else {
        setError(json.error || 'Failed to add')
      }
    } catch (e) {
      setError(e.message || 'Failed to add')
    } finally {
      setAdding(false)
    }
  }

  const handleCsvChange = (e) => {
    const file = e.target?.files?.[0]
    if (!file) return
    setCsvFile(file)
    const reader = new FileReader()
    reader.onload = (ev) => {
      const text = ev.target?.result || ''
      const lines = text.split(/\r?\n/).filter(Boolean)
      const headers = lines[0]?.split(/[,;\t]/).map(h => h.trim()) || []
      const rows = lines.slice(1).map(line => {
        const vals = line.split(/[,;\t]/).map(v => v.trim())
        const row = {}
        headers.forEach((h, i) => { row[h] = vals[i] ?? '' })
        return row
      })
      setCsvPreview(rows.slice(0, 20))
    }
    reader.readAsText(file)
  }

  const handleAddFromCsv = async () => {
    if (!contextId || !csvFile || !isInvestigation) return
    setAdding(true)
    setError(null)
    try {
      const text = await new Promise((res, rej) => {
        const r = new FileReader()
        r.onload = () => res(r.result)
        r.onerror = rej
        r.readAsText(csvFile)
      })
      const lines = String(text).split(/\r?\n/).filter(Boolean)
      const headers = lines[0]?.split(/[,;\t]/).map(h => h.trim().toLowerCase()) || []
      const idx = (n) => headers.findIndex(h => h && h.includes(n))
      const get = (vals, names) => {
        for (const n of names) {
          const i = idx(n)
          if (i >= 0 && vals[i] !== undefined) return vals[i]
        }
        return ''
      }
      const rows = lines.slice(1).map(line => line.split(/[,;\t]/).map(v => v.trim()))
      const itemsToAdd = rows.map((vals, i) => {
        const txId = get(vals, ['transactionpk', 'transaction_id', 'id'])
        const sessId = get(vals, ['sessionpk', 'session_id'])
        const id = txId || sessId || `csv-${i}`
        const type = sessId ? 'session' : 'transaction'
        const payload = {
          [type === 'session' ? 'SessionPK' : 'TransactionPK']: id,
          MemberID: get(vals, ['memberid', 'member_id', 'member_number', 'membership_number']) || null,
          Venue: get(vals, ['venue']) || '',
          EgmHouseNo: get(vals, ['egmhouseno', 'egm', 'egm_house_no']) || '',
          SnapShotTime: get(vals, ['snapshottime', 'timestamp', 'date']) || new Date().toISOString(),
          CashIn: Number(get(vals, ['cashin', 'cash_in'])) || 0,
          CashOut: Number(get(vals, ['cashout', 'cash_out'])) || 0,
          Turnover: Number(get(vals, ['turnover'])) || 0,
        }
        return { type, payload, source_table: 'csv', [type === 'session' ? 'SessionPK' : 'TransactionPK']: id }
      }).filter(it => it.payload.TransactionPK || it.payload.SessionPK)

      const res = await authFetch(getLocalApiUrl(`/api/cases/${contextId}/evidence/add`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: itemsToAdd }),
      })
      const json = await res.json()
      if (json.success) {
        setCsvFile(null)
        setCsvPreview([])
        onLinked?.(json.data)
        onClose?.()
      } else {
        setError(json.error || 'Failed to add from CSV')
      }
    } catch (e) {
      setError(e.message || 'Failed to add from CSV')
    } finally {
      setAdding(false)
    }
  }

  const toggleSessionExpand = (sessionId) => {
    setExpandedSessions(prev => {
      const next = new Set(prev)
      if (next.has(sessionId)) next.delete(sessionId)
      else next.add(sessionId)
      return next
    })
  }

  const isCombinedSessionView = lane === 'combined' && combinedView === 'session'
  const isCombinedIntervalView = lane === 'combined' && combinedView === 'interval'

  const formatItemDate = (it) => {
    if (it.type === 'slot_jackpot') return it.gaming_date || it.trading_day || '—'
    const raw = it.SnapShotTime || it.StartTime || it.SessionStart || it.intervalstart || it.IntervalStart || ''
    return raw ? formatSydneyDateTime(raw) : '—'
  }

  const displayEgm = (it) => it.HouseNumber || it.EgmHouseNo || it.EgmPK || it.egm_pk || it.egm_id || '-'

  const displayMemberNumber = (it) => {
    if (it.type === 'slot_jackpot') return '—'
    if (it.player_type === 'uncarded' || it._memberLabel === 'Uncarded') return 'Uncarded'
    return displayMembershipNumber(it) || it.membership_number || it.member_number || '—'
  }

  const displayPlayer = (it) => {
    if (it.type === 'slot_jackpot') return '—'
    if (it.player_type === 'uncarded' || it._memberLabel === 'Uncarded') return '—'
    return displayPlayerId(it) || it.player_id || it.MemberID || it.member_id || '—'
  }

  const rowAmount = (it) => {
    if (it.type === 'slot_jackpot') {
      return fmt$(it.jackpot_win ?? it.jackpot_win_aud ?? it.amount ?? 0)
    }
    if (it.type === 'combined_session' || it.type === 'combined_interval') {
      return fmt$(it.CreditWin ?? it.creditwin_aud ?? it.CashOut ?? 0)
    }
    return fmt$(it.CashOut || it.CashIn || it.amount || it.Turnover || 0)
  }

  const emptyMessage = lane === 'payouts'
    ? 'No payouts found for this date range. Add a membership # or player ID to narrow results.'
    : lane === 'jackpots'
      ? 'No slot jackpots found for this date range. Add an EGM # to narrow, or import Slot Analysis data.'
      : combinedView === 'session'
        ? 'No combined sessions found for this date range. Add a membership #, player ID, or EGM to narrow, or run Process & Combine for this period.'
        : 'No combined intervals found for this date range.'

  if (!active) return null

  const browsePanel = (
    <>
      {error && <div className="mb-3 p-2 rounded bg-red-50 text-red-700 text-sm">{error}</div>}

      {tab === 'browse' && (
        <>
          <div className="flex gap-2 mb-3">
            {availableLanes.map(l => (
              <button
                key={l}
                type="button"
                onClick={() => setLane(l)}
                className={`px-3 py-1.5 text-xs font-semibold rounded border ${lane === l ? 'text-white border-transparent' : 'bg-white text-slate-600 border-slate-200'}`}
                style={lane === l ? { backgroundColor: P.pri } : {}}
              >
                {l === 'payouts' ? 'Payouts' : l === 'combined' ? 'Combined' : 'Jackpots'}
              </button>
            ))}
          </div>

          {lane === 'jackpots' && (
            <p className="text-xs text-slate-500 mb-3">
              Slot Analysis rows where jackpot win or progressive vouchers &gt; 0. Per EGM per gaming day — not member-specific.
            </p>
          )}

          {lane === 'combined' && (
            <div className="flex gap-4 mb-3 text-sm text-slate-600">
              <label className="flex items-center gap-1.5 cursor-pointer">
                <input type="radio" checked={combinedView === 'session'} onChange={() => setCombinedView('session')} />
                By session <span className="text-xs text-slate-400">(expand intervals)</span>
              </label>
              <label className="flex items-center gap-1.5 cursor-pointer">
                <input type="radio" checked={combinedView === 'interval'} onChange={() => setCombinedView('interval')} />
                By interval <span className="text-xs text-slate-400">(flat list)</span>
              </label>
            </div>
          )}

          {browseHint && (
            <div className="mb-3 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded px-3 py-2">
              {browseHint}
            </div>
          )}

          <div className="flex flex-wrap items-end gap-3 mb-4">
            {lane !== 'jackpots' && (
            <>
            <div className="min-w-[130px]">
              <label className="block text-xs font-medium text-slate-500 mb-1">Membership # <span className="font-normal text-slate-400">(blank = all)</span></label>
              <input
                type="text"
                value={memberFilter}
                onChange={e => setMemberFilter(e.target.value)}
                placeholder={defaultMemberNumber ? String(defaultMemberNumber) : 'Club member #'}
                className="input-field text-sm w-full"
              />
            </div>
            <div className="min-w-[130px]">
              <label className="block text-xs font-medium text-slate-500 mb-1">Player ID <span className="font-normal text-slate-400">(blank = all)</span></label>
              <input
                type="text"
                value={playerIdFilter}
                onChange={e => setPlayerIdFilter(e.target.value)}
                placeholder={defaultPlayerId ? String(defaultPlayerId) : 'IGT player id'}
                className="input-field text-sm w-full"
              />
            </div>
            </>
            )}
            {(lane === 'jackpots' || lane === 'combined') && (
            <div className="min-w-[120px]">
              <label className="block text-xs font-medium text-slate-500 mb-1">EGM # <span className="font-normal text-slate-400">(blank = all)</span></label>
              <input
                type="text"
                value={egmFilter}
                onChange={e => setEgmFilter(e.target.value)}
                placeholder={defaultEgmHouseNo || 'All EGMs'}
                className="input-field text-sm w-full"
              />
            </div>
            )}
            <div className="flex gap-1 pb-0.5">
              <button
                type="button"
                onClick={() => { setDateMode('range'); if (!dateFrom) applyPreset(preset || '1month') }}
                className={`px-2.5 py-1.5 text-xs font-semibold rounded border ${dateMode === 'range' ? 'bg-slate-800 text-white border-slate-800' : 'bg-white text-slate-600 border-slate-200'}`}
              >
                Date range
              </button>
              <button
                type="button"
                onClick={() => {
                  setDateMode('trading_day')
                  setPreset('')
                  if (!tradingDay) setTradingDay(dateTo || localDateStr())
                }}
                className={`px-2.5 py-1.5 text-xs font-semibold rounded border ${dateMode === 'trading_day' ? 'bg-slate-800 text-white border-slate-800' : 'bg-white text-slate-600 border-slate-200'}`}
              >
                Trading day
              </button>
            </div>
            {dateMode === 'trading_day' ? (
              <div>
                <label className="block text-xs font-medium text-slate-500 mb-1">Trading day</label>
                <input
                  type="date"
                  value={tradingDay}
                  onChange={e => setTradingDay(e.target.value)}
                  className="input-field text-sm"
                />
              </div>
            ) : (
              <>
                <div>
                  <label className="block text-xs font-medium text-slate-500 mb-1">From</label>
                  <input type="date" value={dateFrom} onChange={e => { setDateFrom(e.target.value); setPreset('') }} className="input-field text-sm" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-500 mb-1">To</label>
                  <input type="date" value={dateTo} onChange={e => { setDateTo(e.target.value); setPreset('') }} className="input-field text-sm" />
                </div>
                <div className="flex gap-1">
                  {['7days', '2weeks', '1month'].map(p => (
                    <button key={p} type="button" onClick={() => applyPreset(p)} className={`px-3 py-1.5 text-xs font-semibold rounded border ${preset === p ? 'bg-slate-800 text-white border-slate-800' : 'bg-white text-slate-600 border-slate-200'}`}>
                      {p === '7days' ? '7 days' : p === '2weeks' ? '2 weeks' : '1 month'}
                    </button>
                  ))}
                </div>
              </>
            )}
            {lane !== 'jackpots' && (
              <>
                <div>
                  <label className="block text-xs font-medium text-slate-500 mb-1">Time from</label>
                  <select value={hourFrom} onChange={e => setHourFrom(e.target.value)} className="input-field text-sm">
                    <option value="">Any</option>
                    {HOUR_OPTIONS.map(h => <option key={h.value} value={h.value}>{h.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-500 mb-1">Time to</label>
                  <select value={hourTo} onChange={e => setHourTo(e.target.value)} className="input-field text-sm">
                    <option value="">Any</option>
                    {HOUR_OPTIONS.map(h => <option key={h.value} value={h.value}>{h.label}</option>)}
                  </select>
                </div>
              </>
            )}
            {lane === 'combined' && (
              <label className="flex items-center gap-1.5 text-xs text-slate-600 pb-2 cursor-pointer" title="When off, only the searched membership / player id is shown">
                <input type="checkbox" checked={includeUnidentified} onChange={e => setIncludeUnidentified(e.target.checked)} />
                Include uncarded
              </label>
            )}
            <button type="button" onClick={loadLinkable} className="btn-primary text-sm py-1.5 px-3 flex items-center gap-1">
              <Calendar size={14} /> Apply
            </button>
          </div>

          {dateMode === 'trading_day' && (
            <p className="text-xs text-slate-500 mb-2">
              Trading day matches Gaming Data (venue gaming day, typically 3am–3am). Time from/to filter clock hours in venue timezone (e.g. 10:00–12:00 = hours 10, 11, 12).
            </p>
          )}
          {lane === 'jackpots' && (
            <p className="text-xs text-slate-500 mb-2">Jackpots are EGM-day rows — time-of-day filter does not apply.</p>
          )}

          {defaultMemberName && (
            <div className="text-xs text-slate-500 mb-2">Patron: {defaultMemberName}</div>
          )}

          {loading ? (
            <div className="py-8 text-center text-slate-500">Loading…</div>
          ) : (
            <div className="border rounded overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="px-3 py-2 text-left">
                      <input type="checkbox" checked={selected.size === sortedItems.length && sortedItems.length > 0} onChange={selectAll} />
                    </th>
                    {isCombinedSessionView && <th className="px-2 py-2 w-8" />}
                    <SortTh col="id">
                      {isCombinedSessionView ? 'Session' : isCombinedIntervalView ? 'Interval / Session' : 'ID / Subject'}
                    </SortTh>
                    {!isCombinedIntervalView && !isCombinedSessionView && (
                      <SortTh col="type">Type</SortTh>
                    )}
                    {isCombinedSessionView && (
                      <SortTh col="intervals">Intervals</SortTh>
                    )}
                    <SortTh col="member">Membership #</SortTh>
                    <SortTh col="player">Player ID</SortTh>
                    <SortTh col="date">Date</SortTh>
                    <SortTh col="amount">Amount</SortTh>
                    <SortTh col="egm">EGM</SortTh>
                    <SortTh col="venue">Venue</SortTh>
                  </tr>
                </thead>
                <tbody>
                  {sortedItems.map(it => {
                    if (isCombinedSessionView) {
                      const open = expandedSessions.has(it.id)
                      const ivs = Array.isArray(it.intervals) ? it.intervals : []
                      return (
                        <React.Fragment key={it.id}>
                          <tr className={`border-t border-slate-100 hover:bg-slate-50 ${open ? 'bg-slate-50' : ''}`}>
                            <td className="px-3 py-2" onClick={e => e.stopPropagation()}>
                              <input type="checkbox" checked={selected.has(it.id)} onChange={() => toggleSelect(it.id)} />
                            </td>
                            <td className="px-2 py-2 text-slate-400 cursor-pointer" onClick={() => toggleSessionExpand(it.id)}>
                              {ivs.length ? (open ? '▾' : '▸') : ''}
                            </td>
                            <td className="px-3 py-2 font-mono text-xs max-w-[180px] truncate cursor-pointer" title={it.sessionpk} onClick={() => ivs.length && toggleSessionExpand(it.id)}>
                              {it.sessionpk || it.SessionPK}
                            </td>
                            <td className="px-3 py-2">{it.interval_count ?? ivs.length ?? 0}</td>
                            <td className="px-3 py-2">{displayMemberNumber(it)}</td>
                            <td className="px-3 py-2 font-mono text-xs">{displayPlayer(it)}</td>
                            <td className="px-3 py-2">{formatItemDate(it)}</td>
                            <td className="px-3 py-2 font-bold text-red-600">{rowAmount(it)}</td>
                            <td className="px-3 py-2">{displayEgm(it)}</td>
                            <td className="px-3 py-2">{it.Venue || '-'}</td>
                          </tr>
                          {open && ivs.map(iv => (
                            <tr key={iv.id} className="bg-slate-50/80 border-t border-slate-100 text-xs text-slate-600">
                              <td className="px-3 py-1" />
                              <td className="px-2 py-1" />
                              <td className="px-3 py-1 pl-6 font-mono" colSpan={2}>
                                ↳ {String(iv.intervalstart || iv.IntervalStart || '').slice(11, 16)} interval
                              </td>
                              <td className="px-3 py-1">{displayMemberNumber(it)}</td>
                              <td className="px-3 py-1 font-mono">{displayPlayer(it)}</td>
                              <td className="px-3 py-1">{formatItemDate(iv)}</td>
                              <td className="px-3 py-1">{rowAmount(iv)}</td>
                              <td className="px-3 py-1">{displayEgm(it)}</td>
                              <td className="px-3 py-1" />
                            </tr>
                          ))}
                        </React.Fragment>
                      )
                    }
                    return (
                      <tr key={it.id} className="border-t border-slate-100 hover:bg-slate-50">
                        <td className="px-3 py-2"><input type="checkbox" checked={selected.has(it.id)} onChange={() => toggleSelect(it.id)} /></td>
                        <td className="px-3 py-2 font-mono text-xs max-w-[180px] truncate" title={formatRowId(it)}>{formatRowId(it)}</td>
                        {!isCombinedIntervalView && (
                          <td className="px-3 py-2">{typeLabel(it.type)}</td>
                        )}
                        <td className="px-3 py-2">{displayMemberNumber(it)}</td>
                        <td className="px-3 py-2 font-mono text-xs">{displayPlayer(it)}</td>
                        <td className="px-3 py-2">{formatItemDate(it)}</td>
                        <td className="px-3 py-2 font-bold text-red-600">{rowAmount(it)}</td>
                        <td className="px-3 py-2">{displayEgm(it)}</td>
                        <td className="px-3 py-2">{it.Venue || '-'}</td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
              {sortedItems.length === 0 && !loading && (
                <div className="py-8 text-center text-slate-500">
                  {browseHint || emptyMessage}
                </div>
              )}
            </div>
          )}

          {lane === 'combined' && combinedView === 'session' && selected.size > 0 && (
            <p className="mt-2 text-xs text-slate-500">
              Selecting a combined session will link the session summary and all attributed intervals.
            </p>
          )}

          <div className="mt-4 flex justify-end">
            <button type="button" onClick={handleAddSelected} disabled={selected.size === 0 || adding} className="btn-primary flex items-center gap-2">
              {adding ? 'Linking…' : isPayout ? `Accept ${selected.size} selected` : `Link ${selected.size} selected`}
            </button>
          </div>
        </>
      )}

      {tab === 'csv' && csvEnabled && (
        <>
          <div className="mb-4">
            <label className="block text-sm font-medium text-slate-700 mb-2">Upload CSV file</label>
            <input type="file" accept=".csv,.txt" onChange={handleCsvChange} className="block w-full text-sm text-slate-600 file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:bg-slate-100 file:text-slate-700" />
            <p className="text-xs text-slate-500 mt-1">Columns: TransactionPK or SessionPK, member_number, Venue, EgmHouseNo, SnapShotTime, CashIn, CashOut, Turnover</p>
          </div>
          {csvPreview.length > 0 && (
            <div className="border rounded overflow-x-auto mb-4 max-h-48">
              <table className="min-w-full text-xs">
                <thead className="bg-slate-50"><tr>{Object.keys(csvPreview[0] || {}).map(k => <th key={k} className="px-2 py-1 text-left">{k}</th>)}</tr></thead>
                <tbody>{csvPreview.map((row, i) => <tr key={i} className="border-t">{Object.values(row).map((v, j) => <td key={j} className="px-2 py-1">{String(v)}</td>)}</tr>)}</tbody>
              </table>
              {csvPreview.length >= 20 && <div className="px-2 py-1 text-slate-500 text-xs">Showing first 20 rows</div>}
            </div>
          )}
          <div className="flex justify-end">
            <button type="button" onClick={handleAddFromCsv} disabled={!csvFile || adding} className="btn-primary">
              {adding ? 'Adding…' : 'Add from CSV'}
            </button>
          </div>
        </>
      )}
    </>
  )

  if (display === 'embedded') {
    return (
      <div className="space-y-2">
        {browsePanel}
      </div>
    )
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4 sm:p-6" onClick={onClose}>
      <div
        className="bg-white rounded-lg shadow-xl w-full max-w-7xl h-[min(92vh,960px)] overflow-hidden flex flex-col"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-4 py-3 border-b">
          <h3 className="text-lg font-bold text-slate-800">{modalTitle}</h3>
          <button type="button" onClick={onClose} className="p-2 rounded hover:bg-slate-100 text-slate-500">
            <X size={18} />
          </button>
        </div>

        <div className="flex border-b">
          <button
            type="button"
            onClick={() => setTab('browse')}
            className={`flex items-center gap-2 px-4 py-3 text-sm font-semibold ${tab === 'browse' ? 'border-b-2 text-slate-800' : 'text-slate-500'}`}
            style={tab === 'browse' ? { borderBottomColor: P.pri } : {}}
          >
            <Search size={16} /> Browse evidence
          </button>
          {csvEnabled && (
            <button
              type="button"
              onClick={() => setTab('csv')}
              className={`flex items-center gap-2 px-4 py-3 text-sm font-semibold ${tab === 'csv' ? 'border-b-2 text-slate-800' : 'text-slate-500'}`}
              style={tab === 'csv' ? { borderBottomColor: P.pri } : {}}
            >
              <FileUp size={16} /> From CSV
            </button>
          )}
        </div>

        <div className="flex-1 overflow-auto p-4">
          {browsePanel}
        </div>
      </div>
    </div>
  )
}
